/**
 * localStorage-backed persistence. Mirrors the Android `UserSettings` /
 * DataStore approach but uses `localStorage` so no server is required.
 */
import {
  DEFAULT_SETTINGS,
  type PersistedSettings,
} from '../data/settings';

const STORAGE_KEY = 'scrollsnake:settings:v1';

function safeParse(json: string | null): Partial<PersistedSettings> | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as Partial<PersistedSettings>;
  } catch {
    return null;
  }
}

export function loadSettings(): PersistedSettings {
  const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
  const parsed = safeParse(raw) ?? {};
  return {
    soundEnabled: parsed.soundEnabled ?? DEFAULT_SETTINGS.soundEnabled,
    highScore: parsed.highScore ?? DEFAULT_SETTINGS.highScore,
    longestRun: parsed.longestRun ?? DEFAULT_SETTINGS.longestRun,
    furthestLevel: parsed.furthestLevel ?? DEFAULT_SETTINGS.furthestLevel,
  };
}

export function saveSettings(settings: PersistedSettings): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Ignore quota / private-mode failures.
  }
}

export function updateHighScore(settings: PersistedSettings, score: number): {
  settings: PersistedSettings;
  newRecord: boolean;
} {
  if (score <= settings.highScore) return { settings, newRecord: false };
  const next = { ...settings, highScore: score };
  saveSettings(next);
  return { settings: next, newRecord: true };
}

export function recordRunStats(
  settings: PersistedSettings,
  score: number,
  length: number,
  level: number,
): PersistedSettings {
  const next: PersistedSettings = {
    ...settings,
    longestRun: Math.max(settings.longestRun, length),
    furthestLevel: Math.max(settings.furthestLevel, level),
    highScore: Math.max(settings.highScore, score),
  };
  saveSettings(next);
  return next;
}
